import React from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import { Controller, useWatch } from "react-hook-form";
import { adjustToNoon } from "helpers/utils";
import { GradientLabel } from "./PageShell";
import SelectDropdown from "./SelectDropdown";
import UnderlineInput, { UNDERLINE_CLASS } from "./UnderlineInput";
import { currencySymbol } from "./lineCurrency";

export const ADVANCE_SOURCES = [
  { value: "portal", label: "Advance request in the Portal (QF-02)" },
  { value: "legacy", label: "Advance not in the Portal (older data)" },
];

const fmtMoney = (v) => (v === "" || v === null || v === undefined || isNaN(Number(v))) ? "" : Number(v).toLocaleString("en-US");
const parseMoney = (s) => { const c = String(s).replace(/[^\d.]/g, ""); return c === "" ? 0 : Number(c); };
const money = (v) => v.toLocaleString("en-US");

/** Amount advanced, in the request currency: the linked QF-02, or what was typed for an older advance. */
export const advancedAmountOf = ({ source, parentAdvance, legacyAmount, currency }) => {
  if (source === "legacy") return Number(legacyAmount) || 0;
  return Number(currency === "USD" ? parentAdvance?.total_usd : parentAdvance?.total_vnd) || 0;
};

/** Form defaults for a saved QF-03 (an older advance has a reference instead of a linked QF-02). */
export const advanceDefaults = (pr, currency) => ({
  advance_source: pr?.legacy_advance_ref ? "legacy" : "portal",
  legacy_advance_ref: pr?.legacy_advance_ref || "",
  legacy_advance_date: pr?.legacy_advance_date ? new Date(pr.legacy_advance_date) : null,
  legacy_advance_amount: Number(currency === "USD" ? pr?.advanced_amount_usd : pr?.advanced_amount_vnd) || 0,
});

/** Request payload for the advance a QF-03 settles. */
export const advancePayload = (data) => {
  if (data.form_type !== "qf03") return { parent_advance_id: null, legacy_advance_ref: null, legacy_advance_date: null };
  if (data.advance_source !== "legacy") {
    return { parent_advance_id: data.parent_advance?.id || null, legacy_advance_ref: null, legacy_advance_date: null };
  }
  const amount = Number(data.legacy_advance_amount) || 0;
  return {
    parent_advance_id: null,
    legacy_advance_ref: data.legacy_advance_ref?.trim() || null,
    legacy_advance_date: data.legacy_advance_date || null,
    advanced_amount_vnd: data.currency === "USD" ? 0 : amount,
    advanced_amount_usd: data.currency === "USD" ? amount : 0,
  };
};

const Stat = ({ label, value }) => (
  <div>
    <div className="tw-text-[10px] tw-uppercase tw-text-geist-fg-3 tw-font-bold">{label}</div>
    <div className="tw-text-base tw-font-semibold tw-text-primary tw-tabular-nums">{value}</div>
  </div>
);
Stat.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string.isRequired };

/** QF-03: the advance being settled, and Total cost − Advanced = Payable / Receivable. */
const AdvanceToSettle = ({ control, setValue, advances, currency, totalCost }) => {
  const [source, parentAdvance, legacyAmount] = useWatch({
    control, name: ["advance_source", "parent_advance", "legacy_advance_amount"],
  });
  const advanced = advancedAmountOf({ source, parentAdvance, legacyAmount, currency });
  const legacy = source === "legacy";

  return (
    <div className="md:tw-col-span-2 tw-rounded-geist-md tw-border tw-border-solid tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-p-3">
      <GradientLabel required>Advance to settle</GradientLabel>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
        <SelectDropdown aria-label="Where is the advance" value={source || "portal"} options={ADVANCE_SOURCES}
          onChange={(v) => setValue("advance_source", v, { shouldDirty: true })} />
        {!legacy && (
          <SelectDropdown
            aria-label="Advance request to settle"
            value={parentAdvance?.id || ""}
            options={advances.map((a) => ({ value: a.value, label: a.label }))}
            onChange={(v) => {
              const a = advances.find((x) => x.value === v);
              setValue("parent_advance", a ? { id: a.value, code: a.code, payee: a.payee, total_vnd: a.total_vnd, total_usd: a.total_usd } : null, { shouldDirty: true });
            }}
            placeholder={advances.length ? "Select an approved advance request..." : "No approved advance requests"}
          />
        )}
      </div>
      {legacy && (
        <div className="tw-mt-3 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
          <div className="tw-min-w-0">
            <GradientLabel required>Advance reference</GradientLabel>
            <Controller control={control} name="legacy_advance_ref" rules={{ validate: (v) => !legacy || !!v?.trim() }}
              render={({ field, fieldState }) => (
                <UnderlineInput {...field} maxLength={100} aria-label="Advance reference" placeholder="AX voucher or paper form no."
                  className={fieldState.error ? "tw-border-geist-error" : ""} />
              )} />
          </div>
          <div className="tw-min-w-0">
            <GradientLabel>Advance date</GradientLabel>
            <Controller control={control} name="legacy_advance_date"
              render={({ field }) => (
                <DatePicker className={UNDERLINE_CLASS} dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy" isClearable
                  maxDate={new Date()} selected={field.value} onChange={(d) => field.onChange(d ? adjustToNoon(d) : null)}
                  customInput={<input autoComplete="off" aria-label="Advance date" />} />
              )} />
          </div>
          <div className="tw-min-w-0">
            <GradientLabel required>Amount advanced ({currency})</GradientLabel>
            <Controller control={control} name="legacy_advance_amount" rules={{ validate: (v) => !legacy || Number(v) > 0 }}
              render={({ field, fieldState }) => (
                <div className="tw-flex tw-items-baseline tw-gap-1">
                  <UnderlineInput inputMode="decimal" aria-label={`Amount advanced (${currency})`} value={fmtMoney(field.value)}
                    onChange={(e) => field.onChange(parseMoney(e.target.value))}
                    className={`tw-text-right tw-tabular-nums ${fieldState.error ? "tw-border-geist-error" : ""}`} />
                  <span aria-hidden="true" className="tw-text-sm tw-text-primary tw-shrink-0">{currencySymbol(currency)}</span>
                </div>
              )} />
          </div>
          <p className="sm:tw-col-span-3 tw-m-0 tw-text-xs tw-text-geist-fg-3">
            For an advance paid before the Portal. Attach its voucher with the documents below.
          </p>
        </div>
      )}
      <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-2 tw-text-center">
        <Stat label="Total cost" value={money(totalCost)} />
        <Stat label="Advanced" value={money(advanced)} />
        <Stat label={totalCost - advanced >= 0 ? "Payable" : "Receivable"} value={money(Math.abs(totalCost - advanced))} />
      </div>
    </div>
  );
};

AdvanceToSettle.propTypes = {
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  advances: PropTypes.arrayOf(PropTypes.object).isRequired,
  currency: PropTypes.string.isRequired,
  totalCost: PropTypes.number.isRequired,
};

export default AdvanceToSettle;
